import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { agendaApi } from '../api/agenda.api'

const QUERY_KEY = ['agenda']

export function useAgendaComplet(etudiantId: number | undefined, date: Date) {
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  return useQuery({
    queryKey: [...QUERY_KEY, etudiantId, dateStr],
    queryFn: () => agendaApi.getAgendaComplet(etudiantId!, date),
    enabled: !!etudiantId,
  })
}

export function useTerminerTache(etudiantId: number, date: Date) {
  const qc = useQueryClient()
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  return useMutation({
    mutationFn: (tacheId: number) => agendaApi.terminerTache(tacheId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QUERY_KEY, etudiantId, dateStr] }),
  })
}

export function useTerminerTacheLangue(etudiantId: number, date: Date) {
  const qc = useQueryClient()
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  return useMutation({
    mutationFn: (tacheLangueId: number) => agendaApi.terminerTacheLangue(tacheLangueId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QUERY_KEY, etudiantId, dateStr] }),
  })
}

export function useAnnulerTache(etudiantId: number, date: Date) {
  const qc = useQueryClient()
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  return useMutation({
    mutationFn: ({ tacheId, timerSeconds }: { tacheId: number; timerSeconds?: number }) =>
      agendaApi.annulerTache(tacheId, timerSeconds),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QUERY_KEY, etudiantId, dateStr] }),
  })
}

export function useAnnulerTacheLangue(etudiantId: number, date: Date) {
  const qc = useQueryClient()
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  return useMutation({
    mutationFn: ({ tacheLangueId, timerSeconds }: { tacheLangueId: number; timerSeconds?: number }) =>
      agendaApi.annulerTacheLangue(tacheLangueId, timerSeconds),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QUERY_KEY, etudiantId, dateStr] }),
  })
}

export function useUpdateTacheTimer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ tacheId, timerSeconds }: { tacheId: number; timerSeconds: number }) =>
      agendaApi.updateTacheTimer(tacheId, timerSeconds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })
}

export function useReporterTache(etudiantId: number, date: Date) {
  const qc = useQueryClient()
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  // Calculer la date du lendemain pour invalider son cache aussi
  const tomorrow = new Date(date)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`
  return useMutation({
    mutationFn: (tacheId: number) => agendaApi.reporterTache(tacheId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, etudiantId, dateStr] })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, etudiantId, tomorrowStr] })
    },
  })
}

export function useRecupererTache(etudiantId: number, viewedDate: Date) {
  const qc = useQueryClient()
  const viewedDateStr = `${viewedDate.getFullYear()}-${String(viewedDate.getMonth() + 1).padStart(2, '0')}-${String(viewedDate.getDate()).padStart(2, '0')}`
  // La tâche revient à aujourd'hui (pas à la veille)
  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  return useMutation({
    mutationFn: (tacheId: number) => agendaApi.recupererTache(tacheId),
    onSuccess: () => {
      // Invalider le jour consulté (tâche disparaît) ET aujourd'hui (tâche réapparaît)
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, etudiantId, viewedDateStr] })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, etudiantId, todayStr] })
    },
  })
}
